import {
  Autocomplete,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import type { Promisable } from "type-fest";

import type { Organisation } from "@thalamos/haven-common";

/**
 * A server-side search the options come from: what has been typed, and where to
 * send it. Without one, the options are the whole list and are filtered here.
 */
export type OrganisationSearch = {
  input: string;
  onInput: (query: string) => Promisable<void>;
};

/**
 * Chooses one organisation from those offered.
 *
 * The choice is held by the caller as the whole object rather than its id, so
 * the field can render the name after a search that produced it has moved on —
 * the options are refetched as somebody types, and an id alone would have
 * nothing to render once the choice left the results.
 */
export const OrganisationPicker = ({
  label,
  organisations,
  search,
  value,
  onChange,
  required,
  sx,
}: {
  label: string;
  organisations: readonly Organisation[];
  search?: OrganisationSearch | undefined;
  value: Organisation | null;
  onChange: (organisation: Organisation | null) => void;
  required?: boolean;
  sx?: SxProps<Theme>;
}) => (
  <Autocomplete
    options={[...organisations]}
    value={value}
    getOptionLabel={(option) => option.name}
    isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
    {...(search === undefined
      ? {}
      : {
          inputValue: search.input,
          // The server has already narrowed the options to the search, so
          // filtering them again here would hide rows it deliberately returned.
          filterOptions: (options: Organisation[]) => options,
          onInputChange: (_event: unknown, input: string, reason: string) => {
            // A selection sets the input to the chosen name, which would
            // otherwise be sent back as a search for it.
            if (reason !== "reset") {
              void search.onInput(input);
            }
          },
        })}
    onChange={(_event, chosen) => {
      onChange(chosen);
    }}
    sx={sx}
    renderInput={(params) => (
      <TextField {...params} label={label} size="small" required={required} />
    )}
  />
);
